/**
 * The seeds kept in the Seed vault (D69, owner's decision 5; design rev 1 §4.4, critique R20; test 30; B137): above the
 * vault's plants, headed "{n} seeds kept" (every kept seed, an emailed order's and a packet kept from a photo alike). Each
 * is a row: its cut-out when it has one, its name (the name the seed was placed under, else what was printed), Grow's
 * suggestion with one Yes for an emailed seed it could not place (RowSuggestion, as the packet review draws it), and
 * opening the packet review's change dialog (RowChangeDialog: plant, type with "Not sure", variety, pre-filled from a kept
 * packet's plant and variety) whose foot is Cancel (left) and "Plan to grow it", which files it, and "Not a seed" (its
 * onRemove), which removes the row and files nothing. A planned seed goes into the vault at Planning with its note (the
 * server's: POST /seed-orders/held/:id/file; a kept packet's cut-out goes onto the plant), a toast says "{name} is on your
 * Plants list. Grow tells you when to start it.", and the vault's count, its plants and the list refresh together, so a row
 * never lingers.
 *
 * A refusal says the server's words (Free's room: the shared FREE_PLANT_LIMIT words); a failure on our side the
 * standard sentence (reported, hello@gardengameplan.com), never "try again".
 *
 * Where: LocationCard (the vault's card). API: GET /seed-orders/held, POST /seed-orders/held/:id/file, DELETE
 * /seed-orders/held/:id (api/seedOrders); nothing of the plant catalogue until a seed's change dialog opens, whose
 * fields then ask the plant picker's answers as they are used (W24 Job B). Words (en/es): locations.vault.*
 * (kept, keptOne, planToGrow, plannedToast, notSeed, fileTitle), packets.changeRow.
 *
 * @module HeldSeeds
 */

import React from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronRight } from 'lucide-react';
import { useTranslation } from '@/shared/hooks/useTranslation';
import { useLocationsContext } from '@/shared/context/LocationsContextDefinition';
import { gardenWritten } from '@/shared/lib/briefFreshness';
import { useToast } from '@/shared/components/ui/use-toast';
import { usePlantsContext } from '@/shared/hooks/usePlantsContext';
import { errorMessage } from '@/shared/lib/errorMessage';
import { FailureText } from '@/shared/components/ServerFailure';
import { EMPTY_CHOICE, usePickerLookup, type CropChoice, type PickerLookup } from '@/shared/components/plant-fields/usePlantPicker';
import { fileHeldSeed, getHeldSeeds, removeHeldSeed, type HeldSeed } from '@/shared/api/seedOrders';
import type { ScannedPacket } from '@/shared/api/packets';
import { RowSuggestion } from '@/features/packets/components/RowSuggestion';
import { RowChangeDialog } from '@/features/packets/components/RowChangeDialog';
import { PacketThumb } from '@/features/packets/components/PacketPicture';
import { heldItemOf, planBlocked } from './lib/vaultView';
import { SEED_ORDERS_HELD_KEY, SEED_ORDERS_MINE_KEY } from './lib/seedOrderQueries';

/**
 * One held seed's line: what the order printed ("Bee Balm · Jacob Cline · 1/64 gram").
 *
 * @param seed - The held seed.
 * @returns The line.
 */
const printedOf = (seed: HeldSeed): string => [seed.kind, seed.variety, seed.amount].filter(Boolean).join(' · ');

/**
 * A kept seed's name in one line: the name it was placed under (a packet kept from a photo, placed), else what was printed.
 *
 * @param seed - The kept seed.
 * @returns The name.
 */
const nameOf = (seed: HeldSeed): string => seed.placedName || printedOf(seed);

/**
 * The change dialog's starting choice for a kept seed: the plant, type and variety it was kept under (Type "Not sure" as
 * the crop and the flag), else nothing chosen. The crop is the plant picker's answer for its type.
 *
 * @param seed - The kept seed.
 * @param lookup - The plant picker's answers (usePickerLookup).
 * @returns The choice.
 */
const choiceOfSeed = (seed: HeldSeed, lookup: PickerLookup): CropChoice => {
  if (seed.plantId == null) return EMPTY_CHOICE;
  const cropId = lookup.cropOf(seed.plantId);
  if (seed.typeUnsure && cropId !== null) return { cropId, plantId: null, varietyId: null, typeUnsure: true };
  return { cropId, plantId: seed.plantId, varietyId: seed.varietyId };
};

/**
 * The kept seeds' block.
 *
 * @param props.count - How many the server says wait (the heading's number until the list is read).
 */
export const HeldSeeds: React.FC<{ count: number }> = ({ count }) => {
  const { reactTranslate: t } = useTranslation();
  const queryClient = useQueryClient();
  const { refreshLocations } = useLocationsContext();
  const { refetchPlants } = usePlantsContext();
  const { data: held = [] } = useQuery({ queryKey: SEED_ORDERS_HELD_KEY, queryFn: getHeldSeeds });
  const [busy, setBusy] = React.useState<number | null>(null);
  const [failed, setFailed] = React.useState<string | null>(null);
  /** The seed whose change dialog is open, and its choice so far. */
  const [editing, setEditing] = React.useState<{ seed: HeldSeed; choice: CropChoice | null; newVariety: string } | null>(null);
  const { toast } = useToast();
  /** The plant picker's answers for the open seed's type, asked only while its dialog is open. */
  const lookup = usePickerLookup([editing?.seed.plantId], editing !== null);
  /** The open dialog's choice: what the gardener changed, else the plant the seed was kept under. */
  const choice = editing ? editing.choice ?? choiceOfSeed(editing.seed, lookup) : EMPTY_CHOICE;
  const shown = held.length || count;

  /** The vault's count, its plants and the list, read again together. */
  const refreshAll = async (): Promise<void> => {
    // A filed seed is a plant: the brief, the outlook and the garden place (Today's doors, B98) read again.
    gardenWritten(queryClient);
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: SEED_ORDERS_HELD_KEY }),
      queryClient.invalidateQueries({ queryKey: SEED_ORDERS_MINE_KEY }),
      refreshLocations(),
      Promise.resolve(refetchPlants()),
    ]);
  };

  /**
   * Runs a held seed's change on the server, then refreshes.
   *
   * @param id - The held row.
   * @param change - The call.
   */
  const run = async (id: number, change: () => Promise<unknown>): Promise<void> => {
    setBusy(id);
    setFailed(null);
    try {
      await change();
      setEditing(null);
      await refreshAll();
    } catch (error) {
      setFailed(errorMessage(error, t('locations.vault.fileFailed')));
    } finally {
      setBusy(null);
    }
  };

  /**
   * Plans a kept seed to grow: filed into the vault at Planning, and said in a toast.
   *
   * @param seed - The kept seed.
   * @param item - As one POST /packets/confirm item (heldItemOf).
   */
  const plan = async (seed: HeldSeed, item: ReturnType<typeof heldItemOf>): Promise<void> => {
    await fileHeldSeed(seed.id, item);
    toast({ title: t('locations.vault.plannedToast', { name: nameOf(seed) }) });
  };

  /**
   * Yes to Grow's suggestion: filed as the packet review files it.
   *
   * @param seed - The held seed.
   * @param placed - The seed as Yes placed it (lib/acceptSuggestion).
   */
  const accept = (seed: HeldSeed, placed: ScannedPacket): Promise<void> => run(seed.id, () => plan(seed, heldItemOf(placed)));

  /** The change dialog's "Plan to grow it": filed with what was chosen. */
  const saveEdit = (): void => {
    if (!editing) return;
    const { seed, newVariety } = editing;
    void run(seed.id, () => plan(seed, heldItemOf({
      plantId: choice.plantId, varietyId: choice.varietyId, newVariety: choice.varietyId === null ? newVariety.trim() || null : null,
      joinKind: seed.kind || null, typeUnsure: Boolean(choice.typeUnsure), cropId: choice.cropId,
    })));
  };

  if (shown === 0) return null;
  return (
    <section aria-label={shown === 1 ? t('locations.vault.keptOne') : t('locations.vault.kept', { count: shown })} className="mb-2 space-y-2">
      <h4 className="text-sm font-semibold">{shown === 1 ? t('locations.vault.keptOne') : t('locations.vault.kept', { count: shown })}</h4>
      <ul className="space-y-2">
        {held.map((seed) => (
          <li key={seed.id} className="rounded-sm border">
            <button
              type="button"
              className="flex min-h-11 w-full items-center gap-2 px-2.5 py-1.5 text-left text-sm disabled:opacity-60"
              onClick={() => setEditing({ seed, choice: null, newVariety: seed.newVariety ?? seed.variety ?? '' })}
              disabled={busy !== null}
              aria-label={t('packets.changeRow', { name: nameOf(seed) })}
            >
              {seed.photo_url && <PacketThumb src={seed.photo_url} className="shrink-0" />}
              <span className="min-w-0 flex-1 font-medium">{nameOf(seed)}</span>
              <ChevronRight className="h-5 w-5 shrink-0 text-muted-foreground" aria-hidden="true" />
            </button>
            <div className="px-2.5 pb-2">
              <RowSuggestion packet={seed} onAccept={(placed) => void accept(seed, placed)} />
            </div>
          </li>
        ))}
      </ul>
      {failed && <p role="alert" className="text-sm text-destructive"><FailureText text={failed} /></p>}
      {editing && (
        <RowChangeDialog
          open
          onClose={() => setEditing(null)}
          title={t('locations.vault.fileTitle')}
          picture={null}
          name={nameOf(editing.seed)}
          value={choice}
          onChange={(next) => setEditing({ ...editing, choice: next })}
          newVariety={choice.plantId !== null && choice.varietyId === null && !choice.typeUnsure
            ? { value: editing.newVariety, onChange: (name) => setEditing({ ...editing, newVariety: name }) } : null}
          sharedBed=""
          bed={null}
          onBed={() => {}}
          beds={[]}
          showBed={false}
          removeLabel={t('locations.vault.notSeed')}
          onRemove={() => void run(editing.seed.id, () => removeHeldSeed(editing.seed.id))}
          doneLabel={t('locations.vault.planToGrow')}
          onDone={saveEdit}
          cancelable
          doneDisabled={planBlocked({ busy: busy !== null, picked: editing.choice !== null, seed: editing.seed, choice, loading: lookup.loading })}
          idPrefix={`held-${editing.seed.id}`}
        />
      )}
    </section>
  );
};
